<script>
import { aawzDate } from '@utils/dates/aawz-date'
import { formatElapsedMsToDuration, getInitials } from '@utils/Format'
import TranscriptStateFeedback from '@modules/crm/components/transcript-state-feedback/transcript-state-feedback.vue'

export default {
  name: 'DetailTabTranscricao',
  components: { TranscriptStateFeedback },
  props: {
    transcript: { type: Object, default: null },
  },
  data() {
    return {
      searchSegment: '',
      getInitials,
    }
  },
  computed: {
    segments() {
      // Mantém a ordem de chegada do backend (append = cronológica entre sessões).
      // NÃO ordenar por timestamp: ele reseta por sessão e ordenar embaralha.
      return (this.transcript && this.transcript.segments) || []
    },
    transcriptStatus() {
      return (this.transcript && this.transcript.status) || ''
    },
    isLive() {
      return this.transcriptStatus === 'in_progress'
    },
    // Usuário logado da plataforma — fonte da foto exibida nas mensagens próprias.
    currentUser() {
      return this.$currentUser() || {}
    },
    avatarUrl() {
      const u = this.currentUser
      return (u && (u.avatar_url || u.avatarUrl)) || null
    },
    // Dono da gravação (assessor): suas falas vão para o lado direito do chat.
    profissionalNome() {
      return (this.transcript && this.transcript.profissional_nome) || ''
    },
    isSearchingSegments() {
      return Boolean((this.searchSegment || '').trim())
    },
    filteredSegments() {
      const term = (this.searchSegment || '').trim().toLowerCase()
      if (!term) return this.segments
      return this.segments.filter((seg) => {
        const text = (seg && seg.text ? String(seg.text) : '').toLowerCase()
        const speaker = (
          seg && seg.speaker ? String(seg.speaker) : ''
        ).toLowerCase()
        return text.includes(term) || speaker.includes(term)
      })
    },
    // Cada segmento vira uma mensagem própria (bolha por mensagem, estilo do
    // chat de Atendimento) — NÃO agrupamos o texto de um locutor num bloco só.
    // `first` marca o início de uma sequência do mesmo locutor: só aí mostramos
    // avatar + nome (mensagens seguintes do mesmo locutor ficam só com a bolha).
    messages() {
      const out = []
      let prevSpeaker = null
      this.filteredSegments.forEach((seg, idx) => {
        const speaker = (seg && seg.speaker) || 'Desconhecido'
        out.push({
          key: idx,
          speaker,
          text: seg.text,
          timestamp: seg.timestamp,
          own: this.isOwner(speaker),
          self: this.isCurrentUser(speaker),
          first: speaker !== prevSpeaker,
        })
        prevSpeaker = speaker
      })
      return out
    },
    // Estado exibido quando não há mensagens: busca sem resultado, aguardando
    // (reunião ao vivo) ou vazio. Retorna já as props do TranscriptStateFeedback.
    emptyFeedback() {
      if (this.segments.length && this.isSearchingSegments) {
        return {
          state: 'empty',
          icon: 'mdi-text-search',
          message: 'Nenhum trecho encontrado',
        }
      }
      if (this.isLive) {
        return {
          state: 'processing',
          icon: 'mdi-microphone-off',
          message: 'Aguardando fala… a transcrição aparece em tempo real.',
        }
      }
      return {
        state: 'empty',
        icon: 'mdi-microphone-off',
        message: 'Nenhum segmento de transcrição disponível',
      }
    },
  },
  methods: {
    clearSearch() {
      this.searchSegment = ''
    },
    normalizeName(name) {
      return String(name || '')
        .trim()
        .toLowerCase()
        .replaceAll(/\s+/g, ' ')
    },
    // Locutor é o dono da gravação? (alinha a fala à direita)
    isOwner(speaker) {
      const prof = this.normalizeName(this.profissionalNome)
      if (!prof || !speaker) return false
      return this.normalizeName(speaker) === prof
    },
    // Locutor é o próprio usuário logado? (exibe a foto da plataforma)
    isCurrentUser(speaker) {
      const me = this.normalizeName(this.currentUser && this.currentUser.nome)
      if (!me || !speaker) return false
      return this.normalizeName(speaker) === me
    },
    // Compartilhado com transcricaoTxt.js (export .txt) para tela e arquivo
    // nunca divergirem no mesmo cálculo.
    formatSegmentTime(timestampMs) {
      return formatElapsedMsToDuration(timestampMs)
    },
    segmentClock(timestampMs) {
      // Horário absoluto = start_time + tempo decorrido do segmento.
      if (timestampMs === null || timestampMs === undefined) return ''
      if (!this.transcript || !this.transcript.start_time) return ''
      // Backend serializa start_time como 'DD/MM/YYYY HH:mm' (day-first).
      const seconds = Math.floor(Number(timestampMs) / 1000)
      const base = aawzDate(this.transcript.start_time, 'DD/MM/YYYY HH:mm')
      if (!base.isValid()) return ''
      return base.add(seconds, 'seconds').format('HH:mm:ss')
    },
  },
}
</script>

<template>
  <div>
    <div
      class="d-flex flex-wrap align-center justify-space-between mb-4"
      style="gap: 10px"
    >
      <div class="d-flex align-center" style="gap: 10px">
        <div
          v-if="isLive"
          class="detail-tab-transcricao__live-badge text-color-100"
        >
          <span class="detail-tab-transcricao__live-dot"></span>
          AO VIVO
        </div>
        <span v-if="isLive" class="az-text-small">
          Atualiza automaticamente a cada 10 segundos
        </span>
      </div>
      <input-by-type
        v-if="segments.length"
        v-model="searchSegment"
        type="search"
        placeholder="Buscar na transcrição…"
        style="width: 240px"
      />
    </div>

    <p v-if="segments.length && isSearchingSegments" class="az-text-small mb-3">
      {{ filteredSegments.length }} de {{ segments.length }} trechos
    </p>

    <!-- Chat da reunião — coluna de tempo à esquerda + avatar (padrão do
         gerenciador de tarefas) + nome e balão, em ordem cronológica.
         Tudo dentro de um card cinza + borda (mesmo padrão das outras abas). -->
    <template v-if="messages.length">
      <div class="detail-tab-transcricao__card rounded-sm pa-4">
        <div class="detail-tab-transcricao__chat">
        <div
          v-for="msg in messages"
          :key="msg.key"
          class="detail-tab-transcricao__row"
        >
          <!-- Tempo decorrido (cima) + horário real (baixo) -->
          <div class="detail-tab-transcricao__time-col">
            <span class="az-text-small font-medium text-no-wrap">
              {{ formatSegmentTime(msg.timestamp) }}
            </span>
            <span
              v-if="segmentClock(msg.timestamp)"
              class="az-text-small text-no-wrap detail-tab-transcricao__clock"
            >
              {{ segmentClock(msg.timestamp) }}
            </span>
          </div>

          <!-- Avatar (mesmo design do gerenciador de tarefas) -->
          <div class="detail-tab-transcricao__avatar text-color-100">
            <img
              v-if="msg.self && avatarUrl"
              :src="avatarUrl"
              :alt="getInitials(msg.speaker)"
              class="detail-tab-transcricao__avatar-img"
            />
            <span v-else>{{ getInitials(msg.speaker) }}</span>
          </div>

          <!-- Nome + balão da mensagem -->
          <div class="detail-tab-transcricao__content">
            <span class="az-text-small font-semibold primary--text">
              {{ msg.speaker || 'Desconhecido'
              }}<template v-if="msg.self"> · Você</template>:
            </span>
            <div class="detail-tab-transcricao__bubble">
              <p class="az-text-primary az-size-14 mb-0">{{ msg.text }}</p>
            </div>
          </div>
        </div>
        </div>
      </div>
    </template>

    <!-- Sem mensagens: busca sem resultado / aguardando (ao vivo) / vazio -->
    <TranscriptStateFeedback v-else v-bind="emptyFeedback" />
  </div>
</template>

<style lang="sass" scoped>
// Estilização APENAS de layout (flex, espaçamento, fundo, borda, raio,
// animação). Tamanho/peso/família de texto vêm de `az-text-*`; cor de texto
// vem de `.text-color-*` — ambos aplicados no template, nenhuma classe local
// define texto.
.detail-tab-transcricao
  &__live-badge
    display: inline-flex
    align-items: center
    gap: 5px
    background: var(--danger)
    padding: 2px 8px
    border-radius: 12px

  &__live-dot
    width: 7px
    height: 7px
    background: var(--white)
    border-radius: 50%
    animation: transcricao-pulse 2s infinite

  // ---- Chat da reunião ----
  // Card cinza-claro + borda (mesmo padrão dos demais blocos das abas).
  &__card
    background: #F8F9FA
    border: 1px solid #F0F1F3

  &__chat
    display: flex
    flex-direction: column

  // Linha de mensagem: [tempo] [avatar] [nome + balão], alinhada à esquerda.
  &__row
    display: flex
    align-items: flex-start
    gap: 10px

    & + &
      margin-top: 14px

  // Coluna de tempo à esquerda (decorrido em cima, horário real embaixo).
  &__time-col
    flex-shrink: 0
    width: 60px
    display: flex
    flex-direction: column
    align-items: flex-start
    padding-top: 3px

  &__clock
    opacity: 0.7

  // Avatar — mesmo design do gerenciador de tarefas (círculo primary, iniciais
  // brancas, borda branca). A tipografia branca é intrínseca ao avatar (não é
  // texto de corpo), por isso fica aqui e não em az-text-*.
  &__avatar
    flex-shrink: 0
    width: 30px
    height: 30px
    border-radius: 50%
    background-color: var(--primary)
    display: inline-flex
    align-items: center
    justify-content: center
    font-weight: 600
    font-size: 11px
    border: 2px solid var(--white)
    overflow: hidden

  &__avatar-img
    width: 100%
    height: 100%
    object-fit: cover

  &__content
    flex: 1
    min-width: 0

  // Balão branco + borda suave — contraste com o card cinza sem pesar.
  // Quina superior esquerda reta (sem raio) p/ "apontar" para o avatar.
  &__bubble
    margin-top: 4px
    background: var(--white)
    border: 1px solid #E2E4EA
    border-radius: 0 8px 8px 8px
    padding: 8px 12px

@keyframes transcricao-pulse
  0%, 100%
    opacity: 1
  50%
    opacity: 0.5
</style>
